import React, { useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

const PendingEmailBanner = ({
  currentEmail,
  unconfirmedEmail,
  resendConfirmationUrl,
  cancelEmailChangeUrl,
  confirmationSentAt,
  isAdmin = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  if (!unconfirmedEmail || dismissed) return null;

  const handleResend = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(resendConfirmationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Confirmation email resent!' });
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed to resend' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(cancelEmailChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("[name='csrf-token']")?.content,
        },
      });

      if (response.ok) {
        window.location.reload();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed to cancel' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'An error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-amber-800">Email Change Pending</h3>
          <div className="mt-2 text-sm text-amber-700">
            <p>
              {isAdmin ? 'A' : 'Your'} new email address <span className="font-semibold">{unconfirmedEmail}</span> is awaiting confirmation.
            </p>
            <p className="mt-1">
              {isAdmin
                ? `Current email (${currentEmail}) will remain active until confirmed.`
                : `Your current email (${currentEmail}) will remain active until you confirm the change.`
              }
            </p>
            {confirmationSentAt && (
              <p className="mt-1 text-xs text-amber-600">
                Confirmation sent: {formatDate(confirmationSentAt)}
              </p>
            )}
          </div>

          {message && (
            <div className={`mt-2 text-sm ${message.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
              {message.text}
            </div>
          )}

          <div className="mt-3 flex gap-3">
            <button
              type="button"
              disabled={loading}
              onClick={handleResend}
              className="text-sm font-medium text-amber-800 hover:text-amber-600 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Resend confirmation'}
            </button>
            <span className="text-amber-300">|</span>
            <button
              type="button"
              disabled={loading}
              onClick={handleCancel}
              className="text-sm font-medium text-red-700 hover:text-red-500 disabled:opacity-50"
            >
              Cancel change
            </button>
          </div>
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="inline-flex rounded-md bg-amber-50 p-1.5 text-amber-500 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-2 focus:ring-offset-amber-50"
            >
              <span className="sr-only">Dismiss</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingEmailBanner;

